import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "./LoadingSpinner";
import friendService from "../services/friendService";
import { tintFor } from "../utils/tints";
import { cameFromBack } from "../utils/backNav";
import { getListCache, saveListCache } from "../utils/listCache";
import {
  Faces,
  SectionHeading,
  usePeopleToast,
  errorMessage,
} from "./people/PeopleUI";

const DISMISS_KEY = "pr.people.dismissed";
const CACHE_KEY = "people|suggested";
export function readDismissedPeople() {
  try {
    const value = JSON.parse(localStorage.getItem(DISMISS_KEY));
    return new Set(Array.isArray(value) ? value : []);
  } catch {
    return new Set();
  }
}

export function SuggestionCard({
  person,
  onOpen,
  onAdd,
  onDismiss,
  busy,
  leaving,
  actionLabel = null,
}) {
  return (
    <article
      className={"people-suggestion " + (leaving ? "people-leaving" : "")}
    >
      <div
        className="people-suggestion-photo"
        style={{ background: tintFor(person.userId || person.displayName) }}
      >
        <button
          type="button"
          onClick={onOpen}
          aria-label={"View " + person.displayName}
        >
          {person.avatarUrl ? (
            <img src={person.avatarUrl} alt="" referrerPolicy="no-referrer" />
          ) : (
            (person.displayName || "?")[0].toUpperCase()
          )}
        </button>
        <div className="people-mutuals">
          <Faces people={person.mutuals} />
          <span>
            {person.mutualCount
              ? person.mutualCount + " mutual"
              : person.sharedEventTitle || "New here"}
          </span>
        </div>
        {onDismiss && (
          <button
            type="button"
            className="people-dismiss"
            onClick={onDismiss}
            disabled={leaving}
            aria-label={"Dismiss " + person.displayName}
          >
            ×
          </button>
        )}
      </div>
      <div className="people-suggestion-body">
        <button
          type="button"
          onClick={onOpen}
          className="people-suggestion-name"
        >
          {person.displayName}
        </button>
        <div className="people-meta">
          {[
            person.handle && "@" + person.handle,
            person.location?.toUpperCase(),
          ]
            .filter(Boolean)
            .join(" · ")}
        </div>
        {person.mutualClubName && (
          <span className="people-club-chip">
            {person.mutualClubName}
            {person.mutualClubCount > 1
              ? " +" + (person.mutualClubCount - 1)
              : ""}
          </span>
        )}
        <button
          type="button"
          onClick={onAdd}
          disabled={busy || person.requestSent}
          className={"people-pill " + (person.requestSent ? "is-done" : "")}
        >
          {actionLabel
            ? actionLabel
            : person.requestSent
            ? "Requested ✓"
            : busy
            ? "Sending…"
            : person.relationshipStatus === "request_received"
            ? "View request"
            : "+ Add"}
        </button>
      </div>
    </article>
  );
}

export default function PeopleYouMayKnow({
  limit = 12,
  title = "PEOPLE YOU MAY KNOW",
  onEmpty = "message",
  initialPeople,
  onCount,
}) {
  const navigate = useNavigate();
  const seed = cameFromBack() ? getListCache(CACHE_KEY) : null;
  const [people, setPeople] = useState(seed?.items || initialPeople || []);
  const [isLoading, setIsLoading] = useState(!seed && !initialPeople);
  const [error, setError] = useState(null);
  const [retry, setRetry] = useState(0);
  const [dismissed, setDismissed] = useState(readDismissedPeople);
  const [leaving, setLeaving] = useState(new Set());
  const [busy, setBusy] = useState(new Set());
  const pending = useRef(new Set());
  const timers = useRef(new Set());
  const seeded = useRef(!!seed);
  const [notify, toast] = usePeopleToast();
  useEffect(() => {
    const active = timers.current;
    return () => active.forEach(clearTimeout);
  }, []);
  useEffect(() => {
    if (seeded.current && !retry) {
      seeded.current = false;
      return;
    }
    if (initialPeople && !retry) {
      setPeople(initialPeople);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    friendService
      .getSuggestedFriends(limit)
      .then((result) => {
        if (cancelled) return;
        if (result.success) {
          setPeople(result.data || []);
          saveListCache(CACHE_KEY, { items: result.data || [] });
          setError(null);
        } else setError(errorMessage(result, "Could not load suggestions."));
        setIsLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setError("Could not load suggestions.");
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [limit, initialPeople, retry]);

  const shown = people.filter((p) => !dismissed.has(p.userId));
  useEffect(() => {
    if (!isLoading && !error) onCount?.(shown.length);
  }, [shown.length, isLoading, error, onCount]);

  const handleAdd = async (person) => {
    if (pending.current.has(person.userId) || person.requestSent) return;
    pending.current.add(person.userId);
    setBusy(new Set(pending.current));
    try {
      const result = await friendService.sendFriendRequest(person.userId);
      if (!result.success)
        throw new Error(
          errorMessage(result, "Could not send request. Try again.")
        );
      setPeople((prev) => {
        const items = prev.map((p) =>
          p.userId === person.userId ? { ...p, requestSent: true } : p
        );
        saveListCache(CACHE_KEY, { items });
        return items;
      });
      notify("Request sent to " + person.displayName);
    } catch (e) {
      notify(e.message);
    } finally {
      pending.current.delete(person.userId);
      setBusy(new Set(pending.current));
    }
  };
  const persistDismissal = (id, hide) =>
    setDismissed((prev) => {
      const next = new Set(prev);
      if (hide) next.add(id);
      else next.delete(id);
      try {
        localStorage.setItem(DISMISS_KEY, JSON.stringify([...next]));
      } catch {
        /* storage disabled */
      }
      return next;
    });
  const dismiss = (person) => {
    setLeaving((prev) => new Set(prev).add(person.userId));
    const timer = setTimeout(() => {
      timers.current.delete(timer);
      persistDismissal(person.userId, true);
      setLeaving((prev) => {
        const next = new Set(prev);
        next.delete(person.userId);
        return next;
      });
      notify(person.displayName + " hidden", () =>
        persistDismissal(person.userId, false)
      );
    }, 220);
    timers.current.add(timer);
  };
  if (!isLoading && !error && !shown.length && onEmpty === "hide") return null;
  return (
    <div>
      <SectionHeading
        detail={(isLoading ? "…" : shown.length) + " · By mutual friends"}
      >
        {title}
      </SectionHeading>
      {error && (
        <p className="people-error" role="alert">
          {error}{" "}
          <button type="button" onClick={() => setRetry((v) => v + 1)}>
            Retry
          </button>
        </p>
      )}
      {isLoading ? (
        <LoadingSpinner label="Finding people…" />
      ) : !shown.length ? (
        <p className="people-empty">No suggestions yet.</p>
      ) : (
        <div className="people-suggestions">
          {shown.map((person) => (
            <SuggestionCard
              key={person.userId}
              person={person}
              busy={busy.has(person.userId)}
              leaving={leaving.has(person.userId)}
              onOpen={() =>
                navigate(
                  person.handle
                    ? "/" + person.handle
                    : "/users/" + person.userId
                )
              }
              onAdd={() => handleAdd(person)}
              onDismiss={() => dismiss(person)}
            />
          ))}
        </div>
      )}
      {toast}
    </div>
  );
}
